<script setup lang="ts">
import {
  compareMoney,
  daysBetween,
  formatDate,
  formatMoney,
  isZeroMoney,
  type LocalDate,
  type Money,
  moneyProportion,
  niceMoneyTicks,
  plusDays,
  zeroMoney,
} from '@taler/domain'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

interface HistoryPoint {
  readonly date: LocalDate
  readonly cash: Money
  readonly netWorth: Money
}

const props = defineProps<{ points: readonly HistoryPoint[]; resolutionDays: number }>()
const { t, locale } = useI18n()
const localeCode = computed(() => locale.value as 'en' | 'hu')

const VIEW_WIDTH = 960
const VIEW_HEIGHT = 320
const PAD_LEFT = 76
const PAD_RIGHT = 12
const PAD_TOP = 16
const PAD_BOTTOM = 32
const PLOT_WIDTH = VIEW_WIDTH - PAD_LEFT - PAD_RIGHT
const PLOT_HEIGHT = VIEW_HEIGHT - PAD_TOP - PAD_BOTTOM
const TARGET_Y_TICKS = 5
// Evenly spaced across the date span - unrelated to the money ticks, which are rounded to
// a step a person would pick rather than sliced at equal fractions of the range.
const X_TICK_FRACTIONS = [0, 0.25, 0.5, 0.75, 1] as const

const first = computed(() => props.points[0])
const last = computed(() => props.points.at(-1))
const currency = computed(() => first.value?.netWorth.currency)

interface Bounds {
  readonly min: Money
  readonly max: Money
}

// Rounded to a step a person would choose, not the exact data range sliced into equal
// parts - and, because the step is always round, zero (always in range - see below) lands
// exactly on one of these ticks rather than merely between two of them.
const yTicks = computed(() => {
  const currencyCode = currency.value
  if (currencyCode === undefined) return []
  const values: Money[] = [zeroMoney(currencyCode)]
  for (const point of props.points) {
    values.push(point.netWorth)
    if (point.cash) values.push(point.cash)
  }
  const min = values.reduce((min, value) => (compareMoney(value, min) < 0 ? value : min))
  const max = values.reduce((max, value) => (compareMoney(value, max) > 0 ? value : max))
  return niceMoneyTicks(min, max, TARGET_Y_TICKS)
})

// The two series share one scale on purpose: the relationship between them - net worth
// climbing while cash stays flat, say - is the information, and that only reads when both
// are plotted against the same axis.
const bounds = computed<Bounds | undefined>(() => {
  const ticks = yTicks.value
  if (ticks.length === 0) return undefined
  return { min: ticks[0]!, max: ticks[ticks.length - 1]! }
})

const dateSpanDays = computed(() => {
  if (first.value === undefined || last.value === undefined) return 1
  return Math.max(1, daysBetween(first.value.date, last.value.date))
})

function chartX(date: LocalDate): number {
  if (first.value === undefined) return PAD_LEFT
  const fraction = daysBetween(first.value.date, date) / dateSpanDays.value
  return PAD_LEFT + fraction * PLOT_WIDTH
}

function chartY(value: Money): number {
  const range = bounds.value
  if (range === undefined) return PAD_TOP + PLOT_HEIGHT
  const fraction = moneyProportion(value, range.min, range.max)
  return PAD_TOP + PLOT_HEIGHT - fraction * PLOT_HEIGHT
}

const netWorthPath = computed(() =>
  props.points
    .map((point, index) => `${index === 0 ? 'M' : 'L'}${chartX(point.date)},${chartY(point.netWorth)}`)
    .join(' '),
)

const zeroY = computed(() => {
  const currencyCode = currency.value
  if (currencyCode === undefined || bounds.value === undefined) return undefined
  return chartY(zeroMoney(currencyCode))
})

interface Coord {
  readonly x: number
  readonly y: number
}

// Built per contiguous run of points that actually carry cash, not as one path gated on
// every point having it - a single missing point breaks that one run, not the whole series.
const cashRuns = computed(() => {
  const runs: Coord[][] = []
  let current: Coord[] = []
  for (const point of props.points) {
    if (point.cash) {
      current.push({ x: chartX(point.date), y: chartY(point.cash) })
    } else if (current.length > 0) {
      runs.push(current)
      current = []
    }
  }
  if (current.length > 0) runs.push(current)
  return runs
})

function topEdge(run: readonly Coord[]): string {
  return run.map((coord, index) => `${index === 0 ? 'M' : 'L'}${coord.x},${coord.y}`).join(' ')
}

const cashLinePaths = computed(() => cashRuns.value.map(topEdge))

const cashAreaPaths = computed(() => {
  const baseline = zeroY.value
  if (baseline === undefined) return []
  return cashRuns.value.map((run) => {
    const bottom = [...run]
      .reverse()
      .map((coord) => `L${coord.x},${baseline}`)
      .join(' ')
    return `${topEdge(run)} ${bottom} Z`
  })
})

const belowZeroBand = computed(() => {
  const baseline = zeroY.value
  const bottom = VIEW_HEIGHT - PAD_BOTTOM
  if (baseline === undefined || baseline >= bottom) return undefined
  return { y: baseline, height: bottom - baseline }
})

const yAxisTicks = computed(() =>
  yTicks.value.map((value) => ({
    y: chartY(value),
    label: formatMoney(value, { locale: localeCode.value, showSymbol: false }),
    isZero: isZeroMoney(value),
  })),
)

const xTicks = computed(() => {
  const start = first.value
  if (start === undefined) return []
  const span = dateSpanDays.value
  return X_TICK_FRACTIONS.map((fraction) => {
    const date = plusDays(start.date, Math.round(fraction * span))
    return { x: chartX(date), label: formatDate(date, localeCode.value), fraction }
  })
})

const resolutionLabel = computed(() =>
  props.resolutionDays <= 1
    ? t('dashboard.netWorthHistoryResolutionDaily')
    : t('dashboard.netWorthHistoryResolution', { days: props.resolutionDays }),
)

const summaryLabel = computed(() => {
  if (first.value === undefined || last.value === undefined) return ''
  return t('dashboard.netWorthHistoryLabel', {
    from: formatDate(first.value.date, localeCode.value),
    to: formatDate(last.value.date, localeCode.value),
    fromValue: formatMoney(first.value.netWorth, { locale: localeCode.value }),
    toValue: formatMoney(last.value.netWorth, { locale: localeCode.value }),
  })
})
</script>

<template>
  <div v-if="first && last" class="flex flex-col gap-2">
    <div class="flex flex-wrap items-baseline justify-between gap-2">
      <h2 class="text-[length:var(--text-step-4)] font-semibold text-[color:var(--color-text)]">
        {{ t('dashboard.netWorthHistoryTitle') }}
      </h2>
      <div class="flex items-center gap-[var(--space-group)]">
        <span
          class="flex items-center gap-[var(--space-tight)] text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
        >
          <svg width="12" height="8" aria-hidden="true">
            <line x1="0" y1="4" x2="12" y2="4" stroke="var(--color-text)" stroke-width="2" />
          </svg>
          {{ t('dashboard.netWorth') }}
        </span>
        <span
          class="flex items-center gap-[var(--space-tight)] text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
        >
          <svg width="12" height="8" aria-hidden="true">
            <rect width="12" height="8" fill="var(--color-text-muted)" fill-opacity="0.3" />
          </svg>
          {{ t('dashboard.cash') }}
        </span>
      </div>
    </div>

    <svg
      role="img"
      :aria-label="summaryLabel"
      :viewBox="`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`"
      preserveAspectRatio="none"
      class="h-80 w-full"
    >
      <!-- Below zero is the one region with a colour: a net worth or a cash figure under it is
           against the household, so it gets the negative verdict's tint - the series themselves
           are levels, drawn in ink. -->
      <rect
        v-if="belowZeroBand"
        :x="PAD_LEFT"
        :y="belowZeroBand.y"
        :width="VIEW_WIDTH - PAD_LEFT - PAD_RIGHT"
        :height="belowZeroBand.height"
        fill="var(--color-negative)"
        fill-opacity="0.07"
        data-region="below-zero"
      />
      <g v-for="tick in yAxisTicks" :key="`y-${tick.y}`">
        <line
          :x1="PAD_LEFT"
          :x2="VIEW_WIDTH - PAD_RIGHT"
          :y1="tick.y"
          :y2="tick.y"
          :stroke="tick.isZero ? 'var(--color-text-muted)' : 'var(--color-border)'"
          :stroke-width="tick.isZero ? 1.5 : 1"
        />
        <text
          :x="PAD_LEFT - 8"
          :y="tick.y"
          text-anchor="end"
          dominant-baseline="middle"
          :fill="tick.isZero ? 'var(--color-text)' : 'var(--color-text-muted)'"
          :font-weight="tick.isZero ? 600 : 400"
          font-size="10"
          data-axis="y"
        >
          {{ tick.label }}
        </text>
      </g>

      <path
        v-for="(area, index) in cashAreaPaths"
        :key="`cash-fill-${index}`"
        :d="area"
        fill="var(--color-text-muted)"
        fill-opacity="0.14"
        stroke="none"
        data-series="cash"
      />

      <path
        v-for="(line, index) in cashLinePaths"
        :key="`cash-line-${index}`"
        :d="line"
        fill="none"
        stroke="var(--color-text-muted)"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />

      <path
        :d="netWorthPath"
        fill="none"
        stroke="var(--color-text)"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        data-series="net-worth"
      />

      <g v-for="tick in xTicks" :key="`x-${tick.x}`">
        <text
          :x="tick.x"
          :y="VIEW_HEIGHT - PAD_BOTTOM + 18"
          :text-anchor="tick.fraction === 0 ? 'start' : tick.fraction === 1 ? 'end' : 'middle'"
          fill="var(--color-text-muted)"
          font-size="10"
          data-axis="x"
        >
          {{ tick.label }}
        </text>
      </g>
    </svg>

    <p class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">{{ resolutionLabel }}</p>
  </div>
</template>
